import { type ReactNode } from 'react';

import { css } from '@compiled/react';

import { media } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { wacToken } from '@/libraries/design/foundations';
import { DataEventProps } from '@/libraries/wpl/foundations/src/data-event-props';

import { Image } from '../../image/src/image';
import { Link } from '../../link/src/link';
import { Lozenge, type LozengeAppearance } from '../../lozenge/src/lozenge';

export interface BlogNewsCardProps {
	color: BlogNewsCardColor;
	label: string;
	title: string;
	link: {
		href: string;
		text: string;
		dataEventProps?: DataEventProps;
	};
	image: {
		src: string;
		alt: string;
	};
	/** Slot rendered between the lozenge and the title (used by BlogEventCard). */
	eventMeta?: ReactNode;
	motionConfig?: {
		transformForward: string;
		transformBackward: string;
	};
}

export type BlogNewsCardColor = 'purple' | 'green' | 'orange' | 'blue';

export const BlogNewsCard = (props: BlogNewsCardProps) => {
	const { color, label, title, link, image, eventMeta, motionConfig } = props;

	const bgColor = colorToToken[color];
	const ariaLabel = `${link.text}: ${title}`;

	return (
		<div
			css={[
				cardStyles,
				motionConfig?.transformForward &&
					css({
						transform: motionConfig?.transformForward,
						['@container style(--gc-scroll-direction <= 0)']: {
							transform: motionConfig?.transformBackward,
						},
					}),
			]}
		>
			<div
				css={imageWrapperStyles}
				className="blog-news-card-media"
				style={{ backgroundColor: bgColor }}
			>
				<Image src={image.src} alt={image.alt} className="blog-news-card-image" />
			</div>
			{/* eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop */}
			<div css={contentStyles} style={{ backgroundColor: bgColor }}>
				<Lozenge appearance={colorToLozenge[color]} text={label} />
				{eventMeta}
				<div css={titleStyles} className="card-title-sm">
					{title}
				</div>
				<div css={linkSlotStyles}>
					<Link
						type="primary"
						bgColor="bgBlackBlue"
						href={link.href}
						text={link.text}
						aria-label={ariaLabel}
						ariaLabel={ariaLabel}
						className="blog-news-card-link"
						dataEventProps={link.dataEventProps}
					/>
				</div>
			</div>
		</div>
	);
};

const colorToToken: Record<BlogNewsCardColor, string> = {
	purple: wacToken('color.extended.purple800'),
	green: wacToken('color.extended.lime800'),
	orange: wacToken('color.extended.orange700'),
	blue: wacToken('color.extended.blue800'),
};

const colorToLozenge: Record<BlogNewsCardColor, LozengeAppearance> = {
	purple: 'purple',
	green: 'lime',
	orange: 'saffron',
	blue: 'blue',
};

const cardStyles = css({
	borderRadius: wacToken('corner.radius.card'),
	overflow: 'hidden',
	display: 'flex',
	flexDirection: 'column',
	textDecoration: 'none',
	color: 'inherit',
	flexShrink: 0,
	position: 'relative',
	// Figma Blog Card — Desktop 316 / Sm Desktop 280 / Tablet 266 / Mobile 311
	width: '316px',
	[media.only.md]: {
		width: '280px',
	},
	[media.only.sm]: {
		width: '266px',
	},
	[media.below.sm]: {
		width: '311px',
	},
	height: 'fit-content',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'&:hover .blog-news-card-image': {
		transform: 'scale(1.1)',
		transitionTimingFunction: wacToken('motion.easing.ease40-out'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'&:hover .blog-news-card-media::after': {
		transform: 'translateY(0)',
		transitionTimingFunction: wacToken('motion.easing.ease40-out'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.blog-news-card-link::after': {
		content: '""',
		position: 'absolute',
		inset: 0,
	},
	// animation hook when in a gallery container with extra motion enabled
	willChange: 'transform',
	transform: `translateX(calc(var(--gc-scroll-speed) * var(--gc-item-i, 0) / 9 * 20rem))`,
	['@container style(--gc-scroll-direction <= 0)']: {
		transform: `translateX(calc(var(--gc-scroll-speed) * (9 - var(--gc-item-i, 0)) / 9 * -20rem))`,
	},
});

const imageWrapperStyles = css({
	overflow: 'hidden',
	position: 'relative',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.blog-news-card-image': {
		width: '100%',
		aspectRatio: '1 / 1',
		objectFit: 'cover',
		display: 'block',
		transition: `transform ${wacToken('motion.delay.250ms')} ${wacToken(
			'motion.easing.ease60-in',
		)}`,
	},
	'&::after': {
		content: '""',
		backgroundColor: 'inherit',
		position: 'absolute',
		insetBlockEnd: 0,
		insetInline: 0,
		height: token('space.150'),
		transform: `translateY(${token('space.150')})`,
		zIndex: 0,
		transition: `transform ${wacToken('motion.delay.250ms')} ${wacToken(
			'motion.easing.ease60-in',
		)}`,
	},
});

const contentStyles = css({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'flex-start',
	width: '100%',
	boxSizing: 'border-box',
	// Figma Blog Card — 16px gap; fixed content height so primary link anchors at bottom
	gap: token('space.200'),
	// Desktop content panel 230
	height: '230px',
	// Figma: Desktop 24; Sm Desktop / Tablet / Mobile 16
	padding: token('space.300'),
	[media.only.md]: {
		// Sm Desktop content panel 210
		height: '210px',
		padding: token('space.200'),
	},
	[media.only.sm]: {
		// Tablet content panel 183
		height: '183px',
		padding: token('space.200'),
	},
	[media.below.sm]: {
		// Mobile content panel 183
		height: '183px',
		padding: token('space.200'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'a:link, a:visited, a:hover, a:active': {
		color: `${wacToken('color.neutral.light0')} !important`,
	},
});

const titleStyles = css({
	// Figma Details title: flex-[1_0_0] — grows so the primary link stays at the bottom
	flex: '1 0 0',
	minHeight: '1px',
	minWidth: '100%',
	overflow: 'hidden',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'div&.card-title-sm': {
		fontFamily: wacToken('font.charlie.display'),
		color: wacToken('color.neutral.light0'),
		// Figma H5 — Desktop 24 / Sm Desktop 22 / Tablet 20 / Mobile 18 (lh 1.24)
		fontSize: '24px',
		lineHeight: 1.24,
		// Hardcode 500: Charlie Display woff/woff2 doesn't match Figma's 600
		fontWeight: 500,
		letterSpacing: 0,
		[media.only.md]: {
			fontSize: '22px',
		},
		[media.only.sm]: {
			fontSize: '20px',
		},
		[media.below.sm]: {
			fontSize: '18px',
		},
	},
});

const linkSlotStyles = css({
	flexShrink: 0,
	marginBlockStart: 'auto',
});
